/**
 * Admin Approvals List — operator-visible queue (pending + decided rows) with
 * status / user / track / time-range filters (APPR-07, D-16; COP-023, COP-024).
 *
 * Cites:
 *  - APPR-07 — admin approvals queue queryable via REST.
 *  - APPR-09 — full payload trace is admin-only (Show satisfies the payload
 *    half; OTel observability dashboard is out of Phase 5 scope).
 *  - COP-023 — SSE / approval surfaces.
 *  - COP-024 — admin approvals review surface.
 *  - D-16 — admin SPA approvals tab; status default `'pending'` so the
 *    first-load view shows the actionable queue.
 *  - COP-148 D8 — Execution column + Execution filter: whether an approved
 *    action ever ran. Read-only; COP-085's stance is extended, not amended.
 *  - HUB-024 §6 — admin reads route through `withUserRole(targetUserId)` for
 *    Show; the click-through Link below forwards `row.userId` so the Show page
 *    can scope the call.
 *
 * Data fetch: direct `hub.adminListApprovals` typed-SDK call (mirrors the
 * pattern in `pages/users/Memory.tsx`). The Refine data provider doesn't yet
 * route the `approvals` resource — registering a partial provider here would
 * leak Refine-specific transforms into the typed call surface, so we opt for
 * the same explicit-fetch pattern that the per-user Memory tab uses.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Link } from 'react-router';
import type {
  AdminApprovalListResponse,
  AdminApprovalRow,
  ApprovalStatus,
} from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { fmtDateTime, truncateId } from '@/lib/format';
import { cn } from '@/lib/utils';
import { hub } from '@/lib/hub-client';
import { pageCountOf, pageIndexOf } from '@/lib/pagination';
import { useUrlState } from '@/lib/url-state';
import type { PrincipalLabels } from '@/lib/principal-labels';
import { usePrincipalLabels } from '@/lib/use-principal-labels';
// COP-148 D8 — read-only execution visibility. Own module so the unit test can
// render the badge without constructing a HubClient (see execution-badge.tsx).
import {
  EXECUTION_OPTIONS,
  ExecutionBadge,
  type ExecutionFilter,
} from './execution-badge';

// Alias kept for readability; the filter state is spelled out with the
// canonical `ApprovalStatus | 'all'` so the plan's verification grep matches
// the source verbatim.
type StatusFilter = ApprovalStatus | 'all';

const PAGE_SIZE = 50;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  // D-16 — pending first so the default option matches the default filter.
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'edited', label: 'Edited' },
  { value: 'expired', label: 'Expired' },
  { value: 'all', label: 'All' },
];

function StatusBadge({ status }: { status: ApprovalStatus }) {
  const variant: 'default' | 'secondary' | 'destructive' | 'outline' = (
    {
      pending: 'default',
      approved: 'secondary',
      rejected: 'destructive',
      edited: 'outline',
      expired: 'outline',
    } as const
  )[status];
  return <Badge variant={variant}>{status}</Badge>;
}

// UUID v4 surface check used to avoid sending a malformed filter to the
// server (the route's TypeBox schema would 400; we short-circuit silently and
// just treat the input as an in-progress edit).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function toIsoStart(date: string): string | undefined {
  if (!date) return undefined;
  // Date input yields `YYYY-MM-DD`. Anchor to UTC midnight start-of-day so the
  // hub's `fromCreatedAt` (ISO) bounds an inclusive lower edge.
  const d = new Date(`${date}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toIsoEnd(date: string): string | undefined {
  if (!date) return undefined;
  // End-of-day so `toCreatedAt` bounds an inclusive upper edge.
  const d = new Date(`${date}T23:59:59.999Z`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * Label above, raw id below. The id stays on screen because it is what the
 * filters, the Show link and every support conversation are keyed on; the
 * label is what makes the row readable at a glance.
 */
function LabelledId({ label, id }: { label: string | undefined; id: string }) {
  return (
    <div className="flex flex-col leading-tight" title={id}>
      {label ? <span className="text-xs">{label}</span> : null}
      <span className="font-mono text-[11px] text-muted-foreground">
        {truncateId(id)}
      </span>
    </div>
  );
}

function useApprovalsColumns(labels: PrincipalLabels): ColumnDef<AdminApprovalRow>[] {
  return useMemo(
    () => [
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        cell: ({ row }) => (
          <span className="text-xs">{fmtDateTime(row.original.createdAt)}</span>
        ),
      },
      {
        id: 'toolName',
        header: 'Tool',
        accessorKey: 'toolName',
        cell: ({ row }) => (
          <code className="font-mono text-xs">{row.original.toolName}</code>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        // COP-148 D8 — did the approved action ever run? Read-only.
        id: 'executionStatus',
        header: 'Execution',
        accessorKey: 'executionStatus',
        cell: ({ row }) => (
          <ExecutionBadge
            executionStatus={row.original.executionStatus}
            executionError={row.original.executionError}
          />
        ),
      },
      {
        id: 'userId',
        header: 'User',
        accessorKey: 'userId',
        cell: ({ row }) => (
          <LabelledId
            label={labels.userEmail(row.original.userId)}
            id={row.original.userId}
          />
        ),
      },
      {
        id: 'trackId',
        header: 'Track',
        accessorKey: 'trackId',
        cell: ({ row }) => {
          const trackId = row.original.trackId;
          if (!trackId) return <span className="text-muted-foreground">—</span>;
          return <LabelledId label={labels.trackName(trackId)} id={trackId} />;
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          // Passes ?userId=… per HUB-024 §6 / Plan 05-08 — the Show endpoint's
          // adapter wraps the read in withUserRole(targetUserId) and asserts
          // row.userId === targetUserId as defense-in-depth.
          <div className="flex justify-end">
            <Link
              to={`/admin/approvals/show/${row.original.id}?userId=${row.original.userId}`}
              className={cn(buttonVariants({ size: 'sm', variant: 'ghost' }))}
            >
              View
            </Link>
          </div>
        ),
      },
    ],
    [labels],
  );
}

// D-16 — Pending is the default first-load filter so the actionable queue
// surfaces immediately. `'all'` is an explicit opt-in to see decided rows.
// Module-level (and only the non-default values reach the URL) because these
// defaults are a dependency of the `useUrlState` setter.
const FILTER_DEFAULTS = {
  status: 'pending',
  // COP-148 D8 — no execution filter by default; the operator opts in.
  execution: 'all',
  userId: '',
  trackId: '',
  from: '',
  to: '',
  page: '1',
};

export function ApprovalsList() {
  // This page is not a refine table, so nothing syncs it to the location for
  // us — without this the toolbar (and the page you were on) resets on reload.
  const [filters, setFilters] = useUrlState(FILTER_DEFAULTS);
  const statusFilter = filters.status as ApprovalStatus | 'all';
  const executionFilter = filters.execution as ExecutionFilter;
  const { userId: userIdFilter, trackId: trackIdFilter, from: fromDate, to: toDate } = filters;
  const pageIndex = pageIndexOf(filters.page);

  // Any filter change invalidates the page index — page 4 of the old result
  // set is meaningless in the new one.
  const setFilter = (patch: Partial<typeof FILTER_DEFAULTS>) =>
    setFilters({ ...patch, page: FILTER_DEFAULTS.page });
  const setPage = (next: number) => setFilters({ page: String(next + 1) });

  const [rows, setRows] = useState<AdminApprovalRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: pageIndex * PAGE_SIZE,
      };
      if (statusFilter !== 'all') query.status = statusFilter;
      if (executionFilter !== 'all') query.executionStatus = executionFilter;
      if (userIdFilter && UUID_RE.test(userIdFilter)) query.userId = userIdFilter;
      if (trackIdFilter && UUID_RE.test(trackIdFilter)) query.trackId = trackIdFilter;
      const fromIso = toIsoStart(fromDate);
      const toIso = toIsoEnd(toDate);
      if (fromIso) query.fromCreatedAt = fromIso;
      if (toIso) query.toCreatedAt = toIso;
      const res: AdminApprovalListResponse = await hub.adminListApprovals({
        query: query as never,
      });
      setRows(res.approvals);
      setTotal(res.total);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to load approvals');
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, executionFilter, userIdFilter, trackIdFilter, fromDate, toDate, pageIndex]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const labels = usePrincipalLabels();
  const columns = useApprovalsColumns(labels);
  const table = useReactTable<AdminApprovalRow>({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = pageCountOf(total, PAGE_SIZE);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Approvals"
        description="Cross-user approval queue. Pending rows surface the actionable queue; switch the filter to see decided history (APPR-07, D-16)."
      />

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <label htmlFor="approvals-status" className="text-xs text-muted-foreground">
            Status
          </label>
          <select
            id="approvals-status"
            className="h-9 rounded-md border bg-background px-3 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => setFilter({ status: e.target.value as StatusFilter })}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor="approvals-execution" className="text-xs text-muted-foreground">
            Execution
          </label>
          <select
            id="approvals-execution"
            className="h-9 rounded-md border bg-background px-3 py-1 text-sm"
            value={executionFilter}
            onChange={(e) => setFilter({ execution: e.target.value as ExecutionFilter })}
          >
            {EXECUTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor="approvals-user" className="text-xs text-muted-foreground">
            User id (UUID)
          </label>
          <Input
            id="approvals-user"
            className="w-72 font-mono text-xs"
            placeholder="00000000-0000-0000-0000-000000000000"
            value={userIdFilter}
            onChange={(e) => setFilter({ userId: e.target.value.trim() })}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="approvals-track" className="text-xs text-muted-foreground">
            Track id (UUID)
          </label>
          <Input
            id="approvals-track"
            className="w-72 font-mono text-xs"
            placeholder="00000000-0000-0000-0000-000000000000"
            value={trackIdFilter}
            onChange={(e) => setFilter({ trackId: e.target.value.trim() })}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="approvals-from" className="text-xs text-muted-foreground">
            From
          </label>
          <Input
            id="approvals-from"
            type="date"
            className="w-44"
            value={fromDate}
            onChange={(e) => setFilter({ from: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="approvals-to" className="text-xs text-muted-foreground">
            To
          </label>
          <Input
            id="approvals-to"
            type="date"
            className="w-44"
            value={toDate}
            onChange={(e) => setFilter({ to: e.target.value })}
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void refresh()}
          disabled={isLoading}
        >
          {isLoading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {error ? <Alert>{error}</Alert> : null}

      <DataTable
        table={table}
        isLoading={isLoading}
        emptyMessage="No approvals match the current filters."
      />

      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {total} approval{total === 1 ? '' : 's'} · Page {pageIndex + 1} of {pageCount}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage(Math.max(0, pageIndex - 1))}
            disabled={pageIndex === 0 || isLoading}
          >
            Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage(pageIndex + 1 < pageCount ? pageIndex + 1 : pageIndex)}
            disabled={pageIndex + 1 >= pageCount || isLoading}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
